import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

import DialogTitle from 'src/common/components/DialogTitle';
import Loader from 'src/common/components/Loader';

import CheckinStep from './steps/CheckinStep';
import FindUserStep from './steps/FindUserStep';
import RegisterStep from './steps/RegisterStep';
import SelectParticipantsStep from './steps/SelectParticipantsStep';
import SummaryStep from './steps/SummaryStep';
import { useUserCheckinWizard } from './useUserCheckinWizard';

interface UserCheckinWizardModalProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  editionId: string;
  initialUserId?: string;
}

export default function UserCheckinWizardModal({
  open,
  onClose,
  onComplete,
  editionId,
  initialUserId,
}: UserCheckinWizardModalProps) {
  const { t } = useTranslation();

  const wizard = useUserCheckinWizard({
    open,
    editionId,
    initialUserId,
    onComplete,
    onClose,
  });

  const stepLabels = [
    t('userCheckinWizard.steps.findUser'),
    t('userCheckinWizard.steps.selectParticipants'),
    t('userCheckinWizard.steps.stepRegister'),
    t('userCheckinWizard.steps.stepCheckIn'),
    t('userCheckinWizard.steps.summary'),
  ];

  const isLastStep = wizard.activeStep === stepLabels.length - 1;
  const isFirstStep = wizard.activeStep === 0;
  const isCheckinStep = wizard.activeStep === 3;
  const checkinBlocksNext = isCheckinStep && !wizard.canAdvanceFromCheckin;

  const nextDisabled =
    wizard.isBusy ||
    (wizard.activeStep === 0 && !wizard.selectedFindOption) ||
    (wizard.activeStep === 1 && wizard.selectedParticipantIds.length === 0) ||
    checkinBlocksNext;

  const handleClose = () => {
    if (!wizard.isBusy) {
      onClose();
    }
  };

  const nextButton = (
    <Button variant="contained" onClick={wizard.handleNext} disabled={nextDisabled}>
      {t('userCheckinWizard.next')}
    </Button>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth aria-labelledby="user-checkin-wizard-title">
      <Loader isLoading={wizard.isBusy} />
      <DialogTitle id="user-checkin-wizard-title" onClose={handleClose}>
        {t('userCheckinWizard.title')}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={wizard.activeStep} sx={{ mb: 3 }}>
          {stepLabels.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 200 }}>
          {wizard.activeStep === 0 ? (
            <FindUserStep
              users={wizard.users}
              unownedParticipants={wizard.unownedParticipants}
              selectedOption={wizard.selectedFindOption}
              onSelectOption={wizard.handleSelectFindOption}
              qrScannerOpen={wizard.qrScannerOpen}
              onOpenQrScanner={wizard.openQrScanner}
              onCloseQrScanner={wizard.closeQrScanner}
              onQrScan={wizard.handleQrScan}
            />
          ) : null}

          {wizard.activeStep === 1 ? (
            <SelectParticipantsStep
              participants={wizard.userParticipants}
              selectedIds={wizard.selectedParticipantIds}
              onSelectedIdsChange={wizard.setSelectedParticipantIds}
              competitionsById={wizard.competitionsById}
              distancesById={wizard.distancesById}
            />
          ) : null}

          {wizard.activeStep === 2 ? (
            <RegisterStep
              activeEdition={wizard.activeEdition}
              unregisteredParticipants={wizard.unregisteredSelected}
              registerDrafts={wizard.registerDrafts}
              onUpdateDraft={wizard.updateRegisterDraft}
              competitionsById={wizard.competitionsById}
              distancesById={wizard.distancesById}
              onRegisterAll={wizard.handleRegisterAll}
              isRegistering={wizard.isRegistering}
              needsRegistration={wizard.needsRegistration}
            />
          ) : null}

          {wizard.activeStep === 3 ? (
            <CheckinStep
              activeEdition={wizard.activeEdition}
              participantsToCheckIn={wizard.registeredToCheckIn}
              checkinDrafts={wizard.checkinDrafts}
              onUpdateDraft={wizard.updateCheckinDraft}
              competitionsById={wizard.competitionsById}
              distancesById={wizard.distancesById}
              registeredCount={wizard.registered.length}
              checkedInCount={wizard.checkedIn.length}
              onCheckInAll={wizard.handleCheckInAll}
              isCheckingIn={wizard.isCheckingIn}
              canCheckIn={wizard.canCheckIn}
              showCheckInRequiredWarning={checkinBlocksNext}
            />
          ) : null}

          {wizard.activeStep === 4 ? (
            <SummaryStep
              summaryRows={wizard.summaryRows}
              priceBreakdown={wizard.priceBreakdown}
            />
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        {!isFirstStep && !isLastStep ? (
          <Button onClick={wizard.handleBack} disabled={wizard.isBusy}>
            {t('userCheckinWizard.back')}
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {isLastStep ? (
          <Button variant="contained" onClick={wizard.handleDone} disabled={wizard.isBusy}>
            {t('userCheckinWizard.done')}
          </Button>
        ) : checkinBlocksNext ? (
          <Tooltip title={t('userCheckinWizard.checkInRequiredBeforeNext')}>
            <span>{nextButton}</span>
          </Tooltip>
        ) : (
          nextButton
        )}
      </DialogActions>
    </Dialog>
  );
}
